/**
 * Product V2 — eSIM Product Page Interactive JS
 * Works with Shopify product data via window.__PRODUCT_VARIANTS
 * and the Shopify product form system.
 */
(function () {
  'use strict';

  /* ================================================================
     0. CONFIGURATION & DATA
     ================================================================ */
  var variants = window.__PRODUCT_VARIANTS || [];
  var productOptions = window.__PRODUCT_OPTIONS || [];
  var shopCurrency = window.__SHOP_CURRENCY || 'AUD';
  var currencySymbol = window.__CURRENCY_SYMBOL || 'AU$';
  var callsTexts = window.__CALLS_TEXTS || '';
  var variantCallsMode = !!window.__VARIANT_CALLS_MODE;
  var variantCalls = window.__VARIANT_CALLS || {};
  // Per-variant mode: show only the selected variant's own label ('' = hide the calls chip).
  // Legacy mode (no per-variant metafields): fall back to the single global label.
  function callsLabelFor(variantId) {
    if (variantCallsMode) return variantCalls[variantId] || '';
    return callsTexts;
  }
  var qty = 1;
  var cartDrawerDelegated = false;
  var _refreshPromise = null;
  var hasMultiOption = productOptions.length > 1;
  var i18n = window.__I18N || {};

  /* ================================================================
     1. VARIANT PARSING — Determine plan type and build selector
     ================================================================ */

  /**
   * Parse a Shopify variant title like "5GB (7 Days)" into structured data.
   * Handles: "5GB (7 Days)", "Unlimited Data (15 Days)", "5GB/day (7 Days)",
   *          "Viettel – 5GB/day (30 Days)", "TrueMove 15GB (7 Days)",
   *          "4G / 10GB Data (30 Days)", "5G / 20GB Data (15 Days)"
   */
  function parseVariantTitle(title) {
    title = (title || '').replace(/\s+/g, ' ').trim();
    // Normalize full-width CJK punctuation → ASCII so variant titles like
    // "5GB (3 Days）" (trailing U+FF09 from a CJK keyboard) still match the
    // "(...)" parser below. Fixes empty Duration tab on Japan eSIM page.
    title = title
      .replace(/[\uFF08]/g, '(')   // （
      .replace(/[\uFF09]/g, ')')   // ）
      .replace(/\u3000/g, ' ')     // full-width space
      .replace(/[\u2013\u2014]/g, '-'); // – —
    var m = title.match(/^(.+?)\s*\((.+?)\)\s*$/);
    var dataLabel, daysLabel, durKey;

    // Priority 1: trailing parens whose content IS a Days value (most specific).
    // e.g. "Viettel - 5GB/day (7 Days)" → data="Viettel - 5GB/day", days="7 Days"
    // e.g. "40GB UK + 6GB EU (30 Days)" → data="40GB UK + 6GB EU", days="30 Days"
    if (m && /\d+\s*Days?/i.test(m[2])) {
      dataLabel = m[1].trim();
      daysLabel = m[2].trim();
      durKey = (m[2] || '').replace(/\s*Days?\s*/i, '').trim();
    // Priority 2: " - " with Days in the second part (no Days-parens at end).
    // e.g. "60GB - 60 Days (1GB/day)" → data="60GB", days="60 Days", "(1GB/day)" is extra
    } else if (title.indexOf(' - ') !== -1 && /\d+\s*Days?/i.test(title.split(' - ')[1])) {
      var dashParts = title.split(' - ');
      dataLabel = dashParts[0].trim();
      daysLabel = dashParts[1].replace(/\s*\(.+?\)\s*$/, '').trim();
      durKey = daysLabel.replace(/\s*Days?\s*/i, '').trim();
    } else if (m) {
      dataLabel = m[1].trim();
      daysLabel = m[2].trim();
      durKey = (m[2] || '').replace(/\s*Days?\s*/i, '').trim();
    } else if (title.indexOf(' - ') !== -1) {
      var dashParts = title.split(' - ');
      dataLabel = dashParts[0].trim();
      daysLabel = dashParts[1].trim();
      durKey = (dashParts[1] || '').replace(/\s*Days?\s*/i, '').trim();
    } else {
      // Try to match "20GB 14Days" or "20GB 14 Days" pattern
      var gbDaysMatch = title.match(/^(.+?GB)\s+(\d+\s*Days?)$/i);
      if (gbDaysMatch) {
        dataLabel = gbDaysMatch[1].trim();
        daysLabel = gbDaysMatch[2].trim();
        durKey = gbDaysMatch[2].replace(/\s*Days?\s*/i, '').trim();
      } else {
        dataLabel = title;
        daysLabel = '';
        durKey = 'default';
      }
    }

    // Extract carrier prefix (e.g., "Viettel –", "TrueMove", "DTAC")
    var carrier = '';
    var carrierMatch = dataLabel.match(/^([A-Za-z]+(?:\s+[A-Za-z]+)?)\s*[–—-]\s*/);
    if (carrierMatch) {
      carrier = carrierMatch[1].trim();
      dataLabel = dataLabel.replace(carrierMatch[0], '').trim();
    } else {
      // Check for carrier names like "TrueMove 15GB" or "DTAC Unlimited"
      var knownCarriers = ['TrueMove', 'DTAC', 'Viettel', 'Vinaphone', 'Optus', 'Telstra', 'Vodafone'];
      for (var ci = 0; ci < knownCarriers.length; ci++) {
        if (dataLabel.indexOf(knownCarriers[ci]) === 0) {
          carrier = knownCarriers[ci];
          dataLabel = dataLabel.replace(knownCarriers[ci], '').trim();
          break;
        }
      }
    }

    // Remove network prefix like "4G / " or "5G / "
    var network = '';
    var netMatch = dataLabel.match(/^(4G|5G)\s*\/\s*/i);
    if (netMatch) {
      network = netMatch[1].toUpperCase();
      dataLabel = dataLabel.replace(netMatch[0], '').trim();
    }

    // Remove trailing "Data" word
    dataLabel = dataLabel.replace(/\s+Data$/i, '').trim();

    // Classify plan type
    var planCategory = 'fixed'; // default
    var lowerData = dataLabel.toLowerCase();
    if (lowerData.indexOf('unlimited') !== -1) {
      planCategory = 'unlimited';
    } else if (lowerData.indexOf('/day') !== -1 || lowerData.indexOf('/ day') !== -1 || lowerData.indexOf('per day') !== -1) {
      planCategory = 'daily';
    }

    // Extract numeric GB for sorting
    var gbNum = 0;
    var gbMatch = dataLabel.match(/(\d+(?:\.\d+)?)\s*GB/i);
    if (gbMatch) gbNum = parseFloat(gbMatch[1]);

    // Extract numeric days for sorting and language-independent grouping
    var daysNum = 0;
    var daysMatch = daysLabel.match(/(\d+)/);
    if (daysMatch) daysNum = parseInt(daysMatch[1]);

    // Multi-locale fallback: when none of the English-centric patterns above
    // produced a daysLabel (e.g. zh "5GB 7天", ja "5GB 7日間", ko "5GB 7일",
    // de "5GB 7 Tage", fr "5GB 7 jours", es "5GB 7 días", th "5GB 7 วัน"),
    // scan the FULL title for "<digits> <day-suffix>" in any of these scripts.
    // Without this, durKey ends up as 'default' for every translated variant,
    // collapsing the duration <select> to a single empty option.
    if (daysNum === 0) {
      var multiLangDay = title.match(
        /(\d+)\s*(天|日間|日间|日|일간|일|Tage|Tag|jours?|días?|dias?|giorni|giorno|dni|dzień|gün|วัน|ngày|hari|đêm|Days?)/i
      );
      if (multiLangDay) {
        daysNum = parseInt(multiLangDay[1]);
        if (!daysLabel) daysLabel = multiLangDay[0].trim();
        // Strip the matched "<n><suffix>" out of the data label if it leaked in
        if (dataLabel && dataLabel.indexOf(multiLangDay[0]) !== -1) {
          dataLabel = dataLabel.replace(multiLangDay[0], '').trim();
        }
      }
    }

    // Override durKey with pure numeric value so grouping works in any language
    durKey = daysNum > 0 ? String(daysNum) : (durKey || 'default');

    return {
      data: dataLabel,
      days: daysLabel,
      durKey: durKey,
      carrier: carrier,
      network: network,
      planCategory: planCategory,
      gbNum: gbNum,
      daysNum: daysNum,
      fullLabel: title
    };
  }

  /**
   * Determine plan type:
   *   TYPE 1 — Multiple durations AND at least one has multiple data options
   *   TYPE 2 — Multiple variants but each duration has exactly one data option
   *   TYPE 3 — Single variant
   *   TYPE 4 — Mixed plan categories (daily + fixed + unlimited)
   *   TYPE 5 — Multi-carrier (different carriers with different plan types)
   */
  function getPlanType(vars) {
    if (!vars || vars.length <= 1) return 3;

    var categories = {};
    var carriers = {};
    vars.forEach(function (v) {
      var p = parseVariantTitle(v.title);
      categories[p.planCategory] = (categories[p.planCategory] || 0) + 1;
      if (p.carrier) carriers[p.carrier] = (carriers[p.carrier] || 0) + 1;
    });

    var catKeys = Object.keys(categories);
    var carrierKeys = Object.keys(carriers);

    // Multi-carrier with different plan types
    if (carrierKeys.length > 1) return 5;

    // Mixed categories — Type 4 ONLY when 'daily' is mixed in (daily plans
    // have per-day pricing that needs its own tab UI).
    // fixed + unlimited alone should fall through to Type 1/2 so that
    // "Unlimited Data" appears as just another Data Allowance option
    // within the matching Duration group (e.g. Indonesia eSIM —
    // Unlimited Data (15 Days) shows up inside the "15 Days" tab).
    if (catKeys.length > 1 && categories.daily) return 4;

    // Single category — use original logic
    var groups = {};
    vars.forEach(function (v) {
      var parsed = parseVariantTitle(v.title);
      var key = parsed.durKey;
      if (!groups[key]) groups[key] = [];
      groups[key].push(v);
    });
    var maxPerDur = Math.max.apply(null, Object.values(groups).map(function (a) { return a.length; }));
    if (maxPerDur > 1) return 1;
    return 2;
  }

  /* ================================================================
     2. BUILD PLAN SELECTOR HTML
     ================================================================ */

  function formatPrice(cents) {
    return (cents / 100).toFixed(2);
  }

  /**
   * Category labels for plan type tabs
   */
  var categoryLabels = {
    daily: { icon: 'calendar_today', label: i18n.daily_plan || 'Daily Plan', desc: i18n.per_day || '/day' },
    fixed: { icon: 'database', label: i18n.fixed_data || 'Fixed Data', desc: i18n.total_label || 'total' },
    unlimited: { icon: 'all_inclusive', label: i18n.unlimited_plan || 'Unlimited', desc: i18n.no_cap || 'no cap' }
  };

  /**
   * Calculate value metric for a variant
   */
  function calcValueMetric(parsed, priceCents) {
    var price = priceCents / 100;
    if (parsed.planCategory === 'daily') {
      return parsed.daysNum > 0 ? (price / parsed.daysNum).toFixed(2) : null;
    } else if (parsed.planCategory === 'unlimited') {
      return parsed.daysNum > 0 ? (price / parsed.daysNum).toFixed(2) : null;
    } else {
      return parsed.gbNum > 0 ? (price / parsed.gbNum).toFixed(2) : null;
    }
  }

  /**
   * Get value label
   */
  function getValueLabel(parsed) {
    if (parsed.planCategory === 'daily' || parsed.planCategory === 'unlimited') {
      return i18n.per_day || '/day';
    }
    return i18n.per_gb || '/GB';
  }

  /**
   * Parse a set of variants into structured data for plan card rendering.
   */
  function parseVariants(vars) {
    return vars.map(function (v) {
      var p = parseVariantTitle(v.title);
      return {
        id: v.id,
        data: p.data,
        days: p.days,
        durKey: p.durKey,
        carrier: p.carrier,
        network: p.network,
        planCategory: p.planCategory,
        gbNum: p.gbNum,
        daysNum: p.daysNum,
        price: formatPrice(v.price),
        priceCents: v.price,
        comparePrice: v.compare_at_price ? formatPrice(v.compare_at_price) : null,
        available: v.available !== false,
        valueMetric: calcValueMetric(p, v.price),
        valueLabel: getValueLabel(p),
        fullLabel: p.fullLabel
      };
    });
  }

  /**
   * Build plan cards HTML for a given set of parsed variants.
   * Returns the inner HTML string (without price bar).
   */
  function buildPlanCardsHtml(planType, parsed) {
    var first = parsed[0];
    var html = '';

    /* ============ TYPE 4 & 5: Mixed categories / Multi-carrier ============ */
    if (planType === 4 || planType === 5) {
      var groupKey = planType === 5 ? 'carrier' : 'planCategory';
      var groups = {};
      var groupOrder = [];
      parsed.forEach(function (v) {
        var key = v[groupKey] || v.planCategory;
        if (!groups[key]) {
          groups[key] = [];
          groupOrder.push(key);
        }
        groups[key].push(v);
      });

      html += '<div class="mv-plan-selector__label">' + (i18n.select_plan_type || 'Select Plan Type') + '</div>';
      html += '<div class="mv-cat-tabs" id="catTabs">';
      groupOrder.forEach(function (key, i) {
        var cls = i === 0 ? 'mv-cat-tab active' : 'mv-cat-tab';
        var info = categoryLabels[key] || { label: key, icon: 'sim_card', desc: '' };
        var label = planType === 5 ? key : info.label;
        html += '<button class="' + cls + '" data-cat="' + key + '">';
        html += '  <span class="material-symbols-outlined" style="font-size:16px;">' + info.icon + '</span>';
        html += '  <span>' + label + '</span>';
        html += '</button>';
      });
      html += '</div>';

      html += '<div class="mv-data-options" id="dataOpts" style="margin-top:10px;">';
      var firstGroupKey = groupOrder[0];
      var isFirstActive = true;

      parsed.forEach(function (v) {
        var key = v[groupKey] || v.planCategory;
        var isFirstGroup = key === firstGroupKey;
        var hiddenCls = isFirstGroup ? '' : ' hidden';
        var activeCls = (isFirstGroup && isFirstActive) ? ' active' : '';
        if (isFirstGroup && isFirstActive) isFirstActive = false;

        var dailyPrice = v.daysNum > 0 ? (v.priceCents / 100 / v.daysNum).toFixed(2) : null;
        var isDuration = /\d+\s*days?/i.test(v.days || '');
        var dataText = (!isDuration && v.days) ? v.data + ' + ' + v.days : v.data;
        html += '<div class="mv-data-opt' + hiddenCls + activeCls + '" data-cat="' + key + '" data-price="' + v.price + '" data-label="' + v.data + '" data-days="' + v.days + '" data-variant-id="' + v.id + '" data-daily="' + (dailyPrice || '') + '">';
        html += '  <div class="mv-opt-data">' + dataText + '</div>';
        if (isDuration && v.days) {
          html += '  <div class="mv-opt-sub" style="font-size:12px;font-weight:600;color:#858281;margin-top:2px;">' + v.days + '</div>';
        }
        html += '  <div class="mv-opt-price">' + currencySymbol + v.price + '</div>';
        html += '</div>';
      });
      html += '</div>';

    /* ============ TYPE 1: Duration groups with data sub-options ============ */
    } else if (planType === 1) {
      var groups = {};
      var durOrder = [];
      parsed.forEach(function (v) {
        if (!groups[v.durKey]) {
          groups[v.durKey] = [];
          durOrder.push({ key: v.durKey, label: v.days });
        }
        groups[v.durKey].push(v);
      });

      durOrder.sort(function (a, b) {
        var aNum = parseInt(a.key) || 0;
        var bNum = parseInt(b.key) || 0;
        return aNum - bNum;
      });

      var isSingleDur = durOrder.length === 1;
      var firstDurActivated = false;

      if (isSingleDur) {
        /* ---- Single Duration: skip tab bar, show as badge + row cards ---- */
        var durLabel = durOrder[0].label;
        html += '<div class="mv-plan-selector__label mv-plan-selector__label--row-header">';
        html += '  <span>' + (i18n.data_allowance || 'Data Allowance') + '</span>';
        if (durLabel) {
          html += '  <span class="mv-dur-badge"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>' + durLabel + '</span>';
        }
        html += '</div>';
        html += '<div class="mv-data-options mv-data-options--rows" id="dataOpts">';
        parsed.forEach(function (v, i) {
          var activeCls = !firstDurActivated ? ' active' : '';
          if (!firstDurActivated) firstDurActivated = true;
          var dailyPrice = v.daysNum > 0 ? (v.priceCents / 100 / v.daysNum).toFixed(2) : null;
          html += '<div class="mv-data-opt mv-data-opt--row' + activeCls + '" data-dur="' + v.durKey + '" data-price="' + v.price + '" data-label="' + v.data + '" data-days="' + v.days + '" data-variant-id="' + v.id + '" data-daily="' + (dailyPrice || '') + '">';
          html += '  <div class="mv-opt-data">' + v.data + '</div>';
          html += '  <div class="mv-opt-price">' + currencySymbol + v.price + '</div>';
          html += '</div>';
        });
        html += '</div>';

      } else {
        /* ---- Multiple Durations: existing tab / dropdown behavior ---- */
        var useDropdown = durOrder.length > 4;
        if (useDropdown) {
          html += '<div class="mv-plan-selector__label">' + (i18n.duration || 'Duration') + '</div>';
          html += '<label class="mv-dur-select">';
          html += '  <select class="mv-dur-select__field" id="durSelect">';
          durOrder.forEach(function (d, i) {
            var selected = i === 0 ? ' selected' : '';
            html += '    <option value="' + d.key + '"' + selected + '>' + d.label + '</option>';
          });
          html += '  </select>';
          html += '  <span class="material-symbols-outlined mv-dur-select__icon">expand_more</span>';
          html += '</label>';
        } else {
          html += '<div class="mv-plan-selector__label" style="display:flex;justify-content:space-between;align-items:center;">' + (i18n.duration || 'Duration');
          html += '  <span class="mv-scroll-hint" id="scrollHint">' + (i18n.swipe || 'Swipe') + ' <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M6 3l5 5-5 5" stroke="#aca9a6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
          html += '</div>';
          html += '<div class="mv-dur-scroll-wrap" id="durWrap">';
          html += '  <div class="mv-dur-tabs" id="durOpts">';
          durOrder.forEach(function (d, i) {
            var cls = i === 0 ? 'mv-dur-tab active' : 'mv-dur-tab';
            html += '    <button class="' + cls + '" data-dur="' + d.key + '">' + d.label + '</button>';
          });
          html += '  </div>';
          html += '</div>';
        }

        html += '<div class="mv-plan-selector__label" style="margin-top:12px;">' + (i18n.data_allowance || 'Data Allowance') + '</div>';
        html += '<div class="mv-data-options" id="dataOpts">';
        parsed.forEach(function (v, i) {
          var isFirstDur = v.durKey === durOrder[0].key;
          var hiddenCls = isFirstDur ? '' : ' hidden';
          var activeCls = '';
          if (isFirstDur && !firstDurActivated) { activeCls = ' active'; firstDurActivated = true; }
          var dailyPrice = v.daysNum > 0 ? (v.priceCents / 100 / v.daysNum).toFixed(2) : null;
          html += '  <div class="mv-data-opt' + hiddenCls + activeCls + '" data-dur="' + v.durKey + '" data-price="' + v.price + '" data-label="' + v.data + '" data-days="' + v.days + '" data-variant-id="' + v.id + '" data-daily="' + (dailyPrice || '') + '">';
          html += '    <div class="mv-opt-data">' + v.data + '</div>';
          html += '    <div class="mv-opt-price">' + currencySymbol + v.price + '</div>';
          if (dailyPrice) {
            html += '    <div class="mv-opt-daily">' + (i18n.only_prefix || 'Only') + ' ' + currencySymbol + dailyPrice + '<span>' + (i18n.per_day || '/day') + '</span></div>';
          }
          html += '  </div>';
        });
        html += '</div>';
      }

    /* ============ TYPE 2: Each duration = one option ============ */
    } else if (planType === 2) {
      var allSameData = parsed.every(function (v) { return v.data === parsed[0].data; });
      var isDaily = parsed[0].planCategory === 'daily';
      var isUnlimited = parsed[0].planCategory === 'unlimited';

      if (allSameData && (isDaily || isUnlimited)) {
        html += '<div class="mv-plan-selector__label">' + (i18n.data_allowance || 'Data Allowance') + '</div>';
        html += '<div class="mv-plan-fixed-label">';
        html += '  <span class="material-symbols-outlined">' + (isDaily ? 'database' : 'all_inclusive') + '</span>';
        html += '  <span>' + parsed[0].data + '</span>';
        html += '</div>';

        html += '<div class="mv-plan-selector__label" style="margin-top:12px;">' + (i18n.duration || 'Duration') + '</div>';
        html += '<div class="mv-dur-pills" id="dataOpts">';

        parsed.forEach(function (v, i) {
          var cls = i === 0 ? 'mv-dur-pill active' : 'mv-dur-pill';
          var dailyPrice = v.daysNum > 0 ? (v.priceCents / 100 / v.daysNum).toFixed(2) : null;
          html += '<button class="' + cls + '" data-price="' + v.price + '" data-label="' + v.data + '" data-days="' + v.days + '" data-variant-id="' + v.id + '" data-daily="' + (dailyPrice || '') + '">' + v.days + '</button>';
        });
        html += '</div>';

      } else {
        var allSameDataStd = parsed.every(function (v) { return v.data === parsed[0].data; });
        if (allSameDataStd && parsed.length > 1) {
          html += '<div class="mv-plan-selector__label">' + (i18n.data_allowance || 'Data Allowance') + '</div>';
          html += '<div class="mv-plan-fixed-label">';
          html += '  <span class="material-symbols-outlined">database</span>';
          html += '  <span>' + parsed[0].data + '</span>';
          html += '</div>';

          html += '<div class="mv-plan-selector__label" style="margin-top:12px;">' + (i18n.duration || 'Duration') + '</div>';
          html += '<div class="mv-dur-pills" id="dataOpts">';
          parsed.forEach(function (v, i) {
            var cls = i === 0 ? 'mv-dur-pill active' : 'mv-dur-pill';
            var dailyPrice = v.daysNum > 0 ? (v.priceCents / 100 / v.daysNum).toFixed(2) : null;
            html += '<button class="' + cls + '" data-price="' + v.price + '" data-label="' + v.data + '" data-days="' + v.days + '" data-variant-id="' + v.id + '" data-daily="' + (dailyPrice || '') + '">' + v.days + '</button>';
          });
          html += '</div>';
        } else {
          html += '<div class="mv-data-options" id="dataOpts">';
          parsed.forEach(function (v, i) {
            var cls = i === 0 ? 'mv-data-opt active' : 'mv-data-opt';
            var dailyPrice = v.daysNum > 0 ? (v.priceCents / 100 / v.daysNum).toFixed(2) : null;
            var isDuration = /\d+\s*days?/i.test(v.days || '');
            var dataText = (!isDuration && v.days) ? v.data + ' + ' + v.days : v.data;
            html += '<div class="' + cls + '" data-price="' + v.price + '" data-label="' + v.data + '" data-days="' + v.days + '" data-variant-id="' + v.id + '" data-daily="' + (dailyPrice || '') + '">';
            html += '  <div class="mv-opt-data">' + dataText + '</div>';
            if (isDuration && v.days) {
              html += '  <div class="mv-opt-sub" style="font-size:12px;font-weight:600;color:#858281;margin-top:2px;">' + v.days + '</div>';
            }
            html += '  <div class="mv-opt-price">' + currencySymbol + v.price + '</div>';
            if (dailyPrice) {
              html += '  <div class="mv-opt-daily">' + (i18n.only_prefix || 'Only') + ' ' + currencySymbol + dailyPrice + '<span>' + (i18n.per_day || '/day') + '</span></div>';
            }
            html += '</div>';
          });
          html += '</div>';
        }
      }

    /* ============ TYPE 3: Single variant ============ */
    } else {
      html += '<div class="mv-data-options" id="dataOpts" style="grid-template-columns: 1fr;">';
      html += '  <div class="mv-data-opt active" data-price="' + first.price + '" data-label="' + first.data + '" data-days="' + first.days + '" data-variant-id="' + first.id + '" style="padding:16px 20px;">';
      html += '    <div class="mv-opt-data" style="font-size:20px;">' + first.data + '</div>';
      html += '    <div class="mv-opt-sub" style="font-size:13px;font-weight:600;color:#858281;margin-top:4px;">' + first.days + '</div>';
      html += '    <div class="mv-opt-price" style="font-size:14px;margin-top:6px;">' + currencySymbol + first.price + '</div>';
      html += '  </div>';
      html += '</div>';
    }

    return html;
  }

  /**
   * Build the price bar HTML for a given first parsed variant.
   */
  function buildPriceBarHtml(first) {
    var firstDaily = first.daysNum > 0 ? (first.priceCents / 100 / first.daysNum).toFixed(2) : '';
    var html = '<div class="mv-price-bar" id="priceBar">';
    html += '  <div>';
    var firstCalls = callsLabelFor(first.id);
    html += '    <div class="mv-price-summary"><strong>' + first.data + '</strong> &middot; <strong>' + first.days + '</strong>' + (firstCalls ? ' &middot; <strong>' + firstCalls + '</strong>' : '') + '</div>';
    if (firstDaily) {
      html += '    <div class="mv-price-daily-summary">' + (i18n.only_prefix || 'Only') + ' ' + currencySymbol + firstDaily + (i18n.per_day || '/day') + '</div>';
    }
    html += '  </div>';
    html += '  <div style="text-align:right;"><div style="font-size:0.65rem;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:2px;">' + (i18n.total_price || 'Total Price') + '</div><span class="mv-price-currency">' + currencySymbol + '</span><span class="mv-price-amount">' + first.price + '</span></div>';
    html += '</div>';
    return html;
  }

  /* ================================================================
     2b. MULTI-OPTION PLAN SELECTOR (e.g., Network Type + Plan)
     ================================================================ */

  /**
   * Build plan cards for a filtered set of variants (used by multi-option selector).
   * Renders plan cards into #optionPlanCards and price bar into #pdSelectedPlan or appends.
   */
  function buildFilteredPlanCards(optionValue) {
    var filtered = variants.filter(function (v) {
      return v.option1 === optionValue;
    });
    if (filtered.length === 0) return;

    var cardsContainer = document.getElementById('optionPlanCards');
    if (!cardsContainer) return;

    // For multi-option variants, the plan title is in option2.
    // Re-map variant titles to option2 for clean parsing.
    var remapped = filtered.map(function (v) {
      var copy = {};
      for (var k in v) { copy[k] = v[k]; }
      // Use option2 as the title for parsing (strips the network prefix)
      if (v.option2) copy.title = v.option2;
      return copy;
    });

    var planType = getPlanType(remapped);
    var parsed = parseVariants(remapped);
    var cardsHtml = buildPlanCardsHtml(planType, parsed);

    cardsContainer.innerHTML = cardsHtml;

    // Price bar
    var first = parsed[0];
    var priceBarHtml = buildPriceBarHtml(first);
    var selectedPlanContainer = document.getElementById('pdSelectedPlan');
    if (selectedPlanContainer) {
      selectedPlanContainer.innerHTML = priceBarHtml;
    } else {
      cardsContainer.innerHTML += priceBarHtml;
    }

    // Re-init plan selector events for the new plan type
    initPlanSelectorEvents(planType);
  }

  /**
   * Build the multi-option plan selector with option tabs + plan cards.
   */
  function buildMultiOptionSelector(container) {
    var firstOption = productOptions[0];
    var optionName = firstOption.name;
    var optionValues = firstOption.values;

    var html = '';
    html += '<div class="mv-plan-selector__label">' + optionName + '</div>';
    html += '<div class="mv-option-tabs" id="optionTabs">';
    optionValues.forEach(function (val, i) {
      var cls = i === 0 ? 'mv-option-tab active' : 'mv-option-tab';
      // Choose icon based on value content
      var icon = 'signal_cellular_alt';
      var valLower = val.toLowerCase();
      if (valLower.indexOf('5g') !== -1) icon = 'rocket_launch';
      else if (valLower.indexOf('4g') !== -1 || valLower.indexOf('lte') !== -1) icon = 'signal_cellular_alt';
      html += '<button class="' + cls + '" data-option-value="' + val + '" type="button">';
      html += '  <span class="material-symbols-outlined" style="font-size:18px;">' + icon + '</span>';
      html += '  <span>' + val + '</span>';
      html += '</button>';
    });
    html += '</div>';

    // Container for dynamically built plan cards
    html += '<div id="optionPlanCards"></div>';

    container.innerHTML = html;

    // Build plan cards for default (first) option value
    buildFilteredPlanCards(optionValues[0]);

    // Tab click events
    var tabContainer = document.getElementById('optionTabs');
    tabContainer.addEventListener('click', function (e) {
      var tab = e.target.closest('.mv-option-tab');
      if (!tab) return;
      tabContainer.querySelectorAll('.mv-option-tab').forEach(function (t) { t.classList.remove('active'); });
      tab.classList.add('active');
      buildFilteredPlanCards(tab.dataset.optionValue);
    });
  }

  function buildPlanSelector() {
    var container = document.getElementById('pdPlanSelector');
    if (!container || variants.length === 0) return;

    // Multi-option products: render option tabs + filtered plan cards
    if (hasMultiOption) {
      buildMultiOptionSelector(container);
      container.classList.add('mv-plan-selector--ready');
      return;
    }

    var planType = getPlanType(variants);
    var parsed = parseVariants(variants);

    // Use extracted functions for single-option plan building
    var html = buildPlanCardsHtml(planType, parsed);
    container.innerHTML = html;

    // Price bar — use the variant that got .active class, not parsed[0]
    var activeOpt = container.querySelector('.mv-data-opt.active');
    var firstVariant = parsed[0];
    if (activeOpt) {
      var activeId = activeOpt.dataset.variantId;
      for (var pi = 0; pi < parsed.length; pi++) {
        if (String(parsed[pi].id) === String(activeId)) { firstVariant = parsed[pi]; break; }
      }
    }
    var priceBarHtml = buildPriceBarHtml(firstVariant);
    var selectedPlanContainer = document.getElementById('pdSelectedPlan');
    if (selectedPlanContainer) {
      selectedPlanContainer.innerHTML = priceBarHtml;
    } else {
      container.innerHTML += priceBarHtml;
    }

    // Initialize plan selector interactions after building
    initPlanSelectorEvents(planType);

    // Sync hidden variant input with active selection
    if (activeOpt) updatePriceDisplay();

    // CLS guard for the plan selector reserved 280-330px via min-height while
    // this JS rendered the variant tiles. Now that real content is in place,
    // mark it ready so the CSS rule can drop the placeholder min-height —
    // products with only one plan (e.g. USA AT&T physical SIM with a single
    // Unlimited 10-day option) otherwise show a 200+ px empty gap below the
    // tile.
    container.classList.add('mv-plan-selector--ready');
  }

  /* ================================================================
     3. PLAN SELECTOR EVENTS
     ================================================================ */

  function updatePriceDisplay() {
    var active = document.querySelector('#dataOpts .mv-data-opt.active') || document.querySelector('#dataOpts .mv-dur-pill.active');
    var unitPrice, total, summaryHtml;

    // Update hidden quantity in the product form (always, regardless of layout)
    var qtyInput = document.getElementById('pdQuantity');
    if (qtyInput) {
      qtyInput.value = qty;
    }

    // --- Compact plan layout (1-2 variants) ---
    var compactPrice = document.getElementById('pdCompactPrice');
    if (compactPrice) {
      // Get unit price from data attribute or active variant
      var activeCompact = document.querySelector('.pd-compact-plan__variant.active');
      if (activeCompact && activeCompact.dataset.variantPriceRaw) {
        unitPrice = parseFloat(activeCompact.dataset.variantPriceRaw);
        compactPrice.dataset.unitPrice = unitPrice;
      } else {
        unitPrice = parseFloat(compactPrice.dataset.unitPrice);
      }
      if (!isNaN(unitPrice)) {
        total = (unitPrice * qty).toFixed(2);
        var amountEl = compactPrice.querySelector('.pd-compact-plan__amount');
        if (amountEl) {
          amountEl.textContent = currencySymbol + total;
        } else {
          compactPrice.textContent = currencySymbol + total;
        }
      }
      // Update summary and daily price
      if (activeCompact) {
        var summaryEl = document.getElementById('pdCompactSummary');
        var dailyEl = document.getElementById('pdCompactDaily');
        var dataLabel = activeCompact.dataset.variantData || '';
        var daysLabel = activeCompact.dataset.variantDays || '';
        if (summaryEl && dataLabel) {
          summaryEl.innerHTML = dataLabel + ' &middot; ' + daysLabel;
        }
        if (dailyEl) {
          var daysNum = parseInt(daysLabel);
          if (!isNaN(daysNum) && daysNum > 0 && unitPrice) {
            var daily = (unitPrice / daysNum).toFixed(2);
            dailyEl.textContent = (i18n.only_prefix || 'Only') + ' ' + currencySymbol + daily + (i18n.per_day || '/day');
            dailyEl.style.display = '';
          } else {
            dailyEl.style.display = 'none';
          }
        }
      }
      return;
    }

    // --- Multi-variant layout (3+ variants) ---
    if (active) {
      unitPrice = parseFloat(active.dataset.price);
      total = (unitPrice * qty).toFixed(2);
      summaryHtml = '<strong>' + active.dataset.label + '</strong> &middot; <strong>' + (active.dataset.days || '') + '</strong>';
      var activeCalls = callsLabelFor(active.dataset.variantId);
      if (activeCalls) summaryHtml += ' &middot; <strong>' + activeCalls + '</strong>';
      if (qty > 1) summaryHtml += ' &times; ' + qty;
    } else {
      unitPrice = 0;
    }

    var priceBar = document.getElementById('priceBar');
    var priceAmountEl = priceBar ? priceBar.querySelector('.mv-price-amount') : document.querySelector('.mv-price-amount');

    if (!priceAmountEl) return;

    if (!priceAmountEl.dataset.unitPrice) {
      priceAmountEl.dataset.unitPrice = priceAmountEl.textContent;
    }

    if (!active) {
      unitPrice = parseFloat(priceAmountEl.dataset.unitPrice);
      if (isNaN(unitPrice)) return;
      total = (unitPrice * qty).toFixed(2);
    }

    if (active) {
      priceAmountEl.dataset.unitPrice = unitPrice;
    }
    priceAmountEl.textContent = total;

    if (priceBar && active) {
      var summaryEl = priceBar.querySelector('.mv-price-summary');
      if (summaryEl) summaryEl.innerHTML = summaryHtml;
      var dailySummary = priceBar.querySelector('.mv-price-daily-summary');
      if (dailySummary) {
        var daily = active.dataset.daily;
        if (daily) {
          dailySummary.textContent = (i18n.only_prefix || 'Only') + ' ' + currencySymbol + daily + (i18n.per_day || '/day');
          dailySummary.style.display = '';
        } else {
          dailySummary.style.display = 'none';
        }
      }
    }

    var variantInput = document.getElementById('pdVariantId');
    if (variantInput && active && active.dataset.variantId) {
      variantInput.value = active.dataset.variantId;
    }
  }

  function initPlanSelectorEvents(planType) {
    // TYPE 4 & 5: Category/Carrier tab switching
    if (planType === 4 || planType === 5) {
      var catTabs = document.getElementById('catTabs');
      if (catTabs) {
        catTabs.addEventListener('click', function (e) {
          var tab = e.target.closest('.mv-cat-tab');
          if (!tab) return;
          var cat = tab.dataset.cat;
          catTabs.querySelectorAll('.mv-cat-tab').forEach(function (t) { t.classList.remove('active'); });
          tab.classList.add('active');

          var dataOpts = document.querySelectorAll('#dataOpts .mv-data-opt');
          var firstVisible = null;
          dataOpts.forEach(function (o) {
            if (o.dataset.cat === cat) {
              o.classList.remove('hidden');
              if (!firstVisible) firstVisible = o;
            } else {
              o.classList.add('hidden');
              o.classList.remove('active');
            }
          });
          if (firstVisible) {
            dataOpts.forEach(function (o) { o.classList.remove('active'); });
            firstVisible.classList.add('active');
            updatePriceDisplay();
          }
        });
      }
    }

    if (planType === 1) {
      var durContainer = document.getElementById('durOpts');
      var durSelect = document.getElementById('durSelect');
      /* Single-duration case: no duration selector rendered. Skip dur setup
         and let the data-option click handler below handle card selection. */
      if (durContainer || durSelect) {
      var durItems = durContainer ? durContainer.querySelectorAll('.mv-data-opt, .mv-dur-tab, .mv-dur-pill') : [];
      var dataOpts = document.querySelectorAll('#dataOpts .mv-data-opt');
      var durWrap = document.getElementById('durWrap');
      var scrollHint = document.getElementById('scrollHint');

      function selectDuration(durKey, clickedEl) {
        if (durItems.length > 0) {
          durItems.forEach(function (d) { d.classList.remove('active'); });
          var targetEl = clickedEl;
          if (!targetEl && durContainer) {
            targetEl = durContainer.querySelector('[data-dur="' + durKey + '"]');
          }
          if (targetEl) {
            targetEl.classList.add('active');
            if (targetEl.classList.contains('mv-dur-tab')) {
              targetEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          }
        }
        if (durSelect && durSelect.value !== durKey) {
          durSelect.value = durKey;
        }
        var firstVisible = null;
        dataOpts.forEach(function (o) {
          if (o.dataset.dur === durKey) {
            o.classList.remove('hidden');
            if (!firstVisible) firstVisible = o;
          } else {
            o.classList.add('hidden');
            o.classList.remove('active');
          }
        });
        if (firstVisible) {
          dataOpts.forEach(function (o) { o.classList.remove('active'); });
          firstVisible.classList.add('active');
          updatePriceDisplay();
        }
      }

      if (durContainer) {
        durContainer.addEventListener('click', function (e) {
          var el = e.target.closest('.mv-data-opt, .mv-dur-tab, .mv-dur-pill');
          if (!el) return;
          selectDuration(el.dataset.dur, el);
        });
      }

      if (durSelect) {
        durSelect.addEventListener('change', function () {
          selectDuration(this.value);
        });
      }

      // Scroll fade hint for pill tabs
      if (durWrap) {
        var durScroll = durWrap.querySelector('.mv-dur-tabs');
        if (durScroll) {
          function checkScrollEnd() {
            var atEnd = durScroll.scrollLeft + durScroll.clientWidth >= durScroll.scrollWidth - 8;
            durWrap.classList.toggle('scrolled-end', atEnd);
            if (scrollHint && atEnd) scrollHint.classList.add('hidden');
          }
          durScroll.addEventListener('scroll', checkScrollEnd);
          // Run immediately: hide gradient/hint for short lists that don't overflow
          checkScrollEnd();
        }
      }

      // Auto-select first duration
      if (durItems.length > 0) {
        selectDuration(durItems[0].dataset.dur, durItems[0]);
      } else if (durSelect) {
        selectDuration(durSelect.value);
      }
      } /* end: if (durContainer || durSelect) */
    }

    // Data option click (all plan types — supports both card and pill layouts)
    var dataOptsContainer = document.getElementById('dataOpts');
    if (dataOptsContainer) {
      dataOptsContainer.addEventListener('click', function (e) {
        var opt = e.target.closest('.mv-data-opt') || e.target.closest('.mv-dur-pill');
        if (!opt || opt.classList.contains('hidden')) return;
        dataOptsContainer.querySelectorAll('.mv-data-opt, .mv-dur-pill').forEach(function (o) { o.classList.remove('active'); });
        opt.classList.add('active');
        updatePriceDisplay();
      });
    }

    updatePriceDisplay();
  }

  /* ================================================================
     4. QUANTITY CONTROLS
     ================================================================ */

  function initQuantity() {
    var minus = document.getElementById('qtyMinus');
    var plus = document.getElementById('qtyPlus');
    var val = document.getElementById('qtyVal');
    if (!minus || !plus || !val) return;

    minus.addEventListener('click', function () {
      if (qty > 1) { qty--; val.value = qty; updatePriceDisplay(); }
    });
    plus.addEventListener('click', function () {
      if (qty < 99) { qty++; val.value = qty; updatePriceDisplay(); }
    });
    val.addEventListener('change', function () {
      var v = parseInt(this.value);
      if (isNaN(v) || v < 1) v = 1;
      if (v > 99) v = 99;
      qty = v; this.value = qty; updatePriceDisplay();
    });
  }

  /* ================================================================
     5. PRODUCT DETAIL TABS (desktop)
     ================================================================ */

  function activateTab(btn, container, focusBtn) {
    var tab = btn.getAttribute('data-tab');
    container.querySelectorAll('.pd-tabs__btn').forEach(function (b) {
      b.classList.remove('active');
      // Sync tab semantics + roving tabindex for assistive tech.
      b.setAttribute('aria-selected', 'false');
      b.setAttribute('tabindex', '-1');
    });
    container.querySelectorAll('.pd-tabs__panel').forEach(function (p) { p.classList.remove('active'); });
    btn.classList.add('active');
    btn.setAttribute('aria-selected', 'true');
    btn.setAttribute('tabindex', '0');
    var panel = container.querySelector('[data-panel="' + tab + '"]');
    if (panel) panel.classList.add('active');
    if (focusBtn) btn.focus();
  }

  function initTabs() {
    document.querySelectorAll('.pd-tabs__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var container = this.closest('.pd-tabs');
        if (!container) return;
        activateTab(this, container, false);
      });

      // Keyboard navigation: Arrow keys move + activate, Home/End jump.
      btn.addEventListener('keydown', function (e) {
        var container = this.closest('.pd-tabs');
        if (!container) return;
        var tabs = Array.prototype.slice.call(container.querySelectorAll('.pd-tabs__btn'));
        var idx = tabs.indexOf(this);
        if (idx < 0) return;
        var next = null;
        if (e.key === 'ArrowRight') {
          next = tabs[(idx + 1) % tabs.length];
        } else if (e.key === 'ArrowLeft') {
          next = tabs[(idx - 1 + tabs.length) % tabs.length];
        } else if (e.key === 'Home') {
          next = tabs[0];
        } else if (e.key === 'End') {
          next = tabs[tabs.length - 1];
        }
        if (next) {
          e.preventDefault();
          activateTab(next, container, true);
        }
      });
    });
  }

  /* ================================================================
     6. FAQ TABS & ACCORDION
     ================================================================ */

  function initFaq() {
    // FAQ category tabs
    document.querySelectorAll('.pd-faq__tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        document.querySelectorAll('.pd-faq__tab').forEach(function (t) { t.classList.remove('active'); });
        document.querySelectorAll('.pd-faq__category').forEach(function (c) { c.classList.remove('active'); });
        this.classList.add('active');
        var target = document.getElementById(this.dataset.faqTab);
        if (target) target.classList.add('active');
      });
    });

    // FAQ accordion handled by inline script in section template
  }

  /* ================================================================
     7. MOBILE ACCORDION (Details/Features)
     ================================================================ */

  function initAccordion() {
    document.querySelectorAll('.pd-accordion__trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var item = this.closest('.pd-accordion__item');
        var body = item.querySelector('.pd-accordion__body');
        var isOpen = item.classList.contains('is-open');
        // Close all
        document.querySelectorAll('.pd-accordion__item').forEach(function (i) {
          i.classList.remove('is-open');
          i.querySelector('.pd-accordion__body').classList.remove('is-open');
          // Sync expanded state on every trigger when collapsing.
          var t = i.querySelector('.pd-accordion__trigger');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
        // Toggle current
        if (!isOpen) {
          item.classList.add('is-open');
          body.classList.add('is-open');
          this.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ================================================================
     9. DEVICE COMPATIBILITY MODAL
     ================================================================ */

  function initDeviceModal() {
    var modal = document.getElementById('dc-modal');
    if (!modal) return;

    var closeBtn = modal.querySelector('.dc-modal-close');
    var overlay = modal.querySelector('.dc-modal-overlay');
    var searchInput = document.getElementById('dc-search-input');
    var searchClear = document.getElementById('dc-search-clear');
    var noResults = modal.querySelector('.dc-no-results');

    function closeModal() {
      modal.classList.remove('active');
      document.body.style.overflow = '';
      if (searchInput) { searchInput.value = ''; }
      if (searchClear) { searchClear.style.display = 'none'; }
      resetSearch();
    }

    function openModal() {
      // Close mobile menu if open
      var mobilePanel = document.getElementById('hdr2MobilePanel');
      if (mobilePanel) mobilePanel.classList.remove('is-open');
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('active')) closeModal();
    });

    // Brand accordion
    modal.querySelectorAll('.dc-brand-header').forEach(function (h) {
      h.addEventListener('click', function () {
        var g = h.closest('.dc-brand-group');
        var isOpen = g.classList.contains('open');
        modal.querySelectorAll('.dc-brand-group.open').forEach(function (x) { x.classList.remove('open'); });
        if (!isOpen) g.classList.add('open');
      });
    });

    function resetSearch() {
      modal.querySelectorAll('.dc-brand-group').forEach(function (g) {
        g.style.display = '';
        g.classList.remove('open');
        g.querySelectorAll('.dc-device-item').forEach(function (i) { i.style.display = ''; });
      });
      if (noResults) noResults.style.display = 'none';
    }

    // Search
    var st;
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        clearTimeout(st);
        st = setTimeout(function () {
          var q = searchInput.value.trim().toLowerCase();
          if (searchClear) searchClear.style.display = q ? 'flex' : 'none';
          if (!q) { resetSearch(); return; }
          var any = false;
          modal.querySelectorAll('.dc-brand-group').forEach(function (g) {
            var match = false;
            g.querySelectorAll('.dc-device-item').forEach(function (i) {
              if (i.dataset.name.indexOf(q) !== -1) {
                i.style.display = '';
                match = true;
              } else {
                i.style.display = 'none';
              }
            });
            if (match) {
              g.style.display = '';
              g.classList.add('open');
              any = true;
            } else {
              g.style.display = 'none';
            }
          });
          if (noResults) noResults.style.display = any ? 'none' : 'block';
        }, 150);
      });
    }

    if (searchClear) {
      searchClear.addEventListener('click', function () {
        if (searchInput) { searchInput.value = ''; searchInput.focus(); }
        searchClear.style.display = 'none';
        resetSearch();
      });
    }

    // Open modal from "Check Device" buttons — only bind if called directly (not deferred)
    // When deferred, the wrapper in initDeferredModals already handles the first open.
  }

  /* ================================================================
     10. CONFIRMATION CHECKBOX — Gate Add to Cart / Buy Now
     ================================================================ */

  function initConfirmCheckbox() {
    var confirmCheck = document.getElementById('pdConfirmCheck');
    var confirmError = document.getElementById('pdConfirmError');
    if (!confirmCheck) return;

    function showError() {
      if (confirmError) confirmError.style.display = 'flex';
      var label = confirmCheck.closest('.pd-confirm__label');
      if (label) { label.style.outline = '2px solid #dc2626'; label.style.borderRadius = '6px'; }
      confirmCheck.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function clearError() {
      if (confirmError) confirmError.style.display = 'none';
      var label = confirmCheck.closest('.pd-confirm__label');
      if (label) { label.style.outline = ''; }
    }

    confirmCheck.addEventListener('change', function () {
      if (this.checked) clearError();
    });

    /*
     * CAPTURE PHASE on document — fires BEFORE any other handler
     * (MINIMOG app.min.js, Shopify dynamic checkout, etc.)
     * This is the only reliable way to block all add-to-cart paths.
     */
    var productForm = document.getElementById('product-form-v2');

    /* Block form submit at capture phase */
    document.addEventListener('submit', function (e) {
      if (!productForm || !productForm.contains(e.target) && e.target !== productForm) return;
      if (!confirmCheck.checked) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showError();
      }
    }, true);

    /* Block click on Add to Cart button at capture phase */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.pd-cart__add, .pd-cart__buy, .pd-cart__dynamic *, .shopify-payment-button *, #pdMorePayment');
      if (!btn) return;
      if (!confirmCheck.checked) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showError();
      }
    }, true);

    /* Also intercept fetch to /cart/add.js as last resort */
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      if (typeof url === 'string' && url.indexOf('/cart/add') !== -1) {
        if (confirmCheck && !confirmCheck.checked) {
          showError();
          return Promise.reject(new Error('Confirm checkbox required'));
        }
      }
      return origFetch.apply(this, arguments);
    };
  }

  /* ================================================================
     11. WISHLIST / ASK / SHARE
     ================================================================ */

  function initActionButtons() {
    // Wishlist — localStorage based (shared with homepage cards via 'sf-wishlist' key)
    var wishBtn = document.getElementById('pdWishlist');
    if (wishBtn) {
      var WISHLIST_KEY = 'sf-wishlist';
      var productHandle = window.__PRODUCT_HANDLE || '';

      function getWishlist() {
        try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch(e) { return []; }
      }
      function saveWishlist(list) {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      }
      function updateBadges() {
        var count = getWishlist().length;
        document.querySelectorAll('.sf-wishlist-count, .ssw-counter-fave-menu').forEach(function(b) {
          if (count > 0) { b.textContent = count; b.style.display = ''; }
          else { b.textContent = ''; b.style.display = 'none'; }
        });
      }
      var _wishLabelOrig = wishBtn.querySelector('span') ? wishBtn.querySelector('span').textContent : 'Add to Wishlist';
      function syncVisual(wishlisted) {
        var label = wishBtn.querySelector('span');
        if (wishlisted) {
          wishBtn.classList.add('is-wishlisted');
          if (label) label.textContent = label.getAttribute('data-wishlisted') || 'Wishlisted';
        } else {
          wishBtn.classList.remove('is-wishlisted');
          if (label) label.textContent = _wishLabelOrig;
        }
      }

      // Sync initial state from localStorage
      if (productHandle) {
        var list = getWishlist();
        syncVisual(list.indexOf(productHandle) >= 0);
      }

      wishBtn.addEventListener('click', function () {
        if (!productHandle) return;
        var list = getWishlist();
        var idx = list.indexOf(productHandle);
        if (idx >= 0) {
          list.splice(idx, 1);
          syncVisual(false);
        } else {
          list.push(productHandle);
          syncVisual(true);
        }
        saveWishlist(list);
        updateBadges();
      });
    }

    // Ask a Question — open modal
    var askBtn = document.getElementById('pdAskQuestion');
    var askModal = document.getElementById('pdAskModal');
    if (askBtn && askModal) {
      var askOverlay = askModal.querySelector('.pd-ask-modal__overlay');
      var askClose = askModal.querySelector('.pd-ask-modal__close');

      function openAskModal() {
        askModal.style.display = 'flex';
        document.body.style.overflow = 'hidden';
      }
      function closeAskModal() {
        askModal.style.display = 'none';
        document.body.style.overflow = '';
      }

      askBtn.addEventListener('click', openAskModal);
      if (askOverlay) askOverlay.addEventListener('click', closeAskModal);
      if (askClose) askClose.addEventListener('click', closeAskModal);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && askModal.style.display === 'flex') closeAskModal();
      });

      // Show success message if form was just submitted
      var askSuccess = askModal.querySelector('.pd-ask-modal__success');
      if (askSuccess) openAskModal();
    }

    // Share Modal
    var shareBtn = document.getElementById('pdShare');
    var shareModal = document.getElementById('pdShareModal');
    if (shareBtn && shareModal) {
      var shareOverlay = shareModal.querySelector('.pd-share-modal__overlay');
      var shareClose = shareModal.querySelector('.pd-share-modal__close');
      var shareUrl = document.getElementById('pdShareUrl');
      var copyBtn = document.getElementById('pdCopyLink');
      var pageUrl = window.location.href;
      var pageTitle = document.title;

      shareBtn.addEventListener('click', function () {
        if (shareUrl) shareUrl.value = pageUrl;
        shareModal.style.display = 'flex';
        document.body.style.overflow = 'hidden';
      });

      function closeShare() {
        shareModal.style.display = 'none';
        document.body.style.overflow = '';
      }

      if (shareOverlay) shareOverlay.addEventListener('click', closeShare);
      if (shareClose) shareClose.addEventListener('click', closeShare);

      shareModal.querySelectorAll('[data-share]').forEach(function (a) {
        a.addEventListener('click', function (e) {
          e.preventDefault();
          var type = this.dataset.share;
          var url = '';
          if (type === 'facebook') url = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(pageUrl);
          if (type === 'twitter') url = 'https://twitter.com/intent/tweet?url=' + encodeURIComponent(pageUrl) + '&text=' + encodeURIComponent(pageTitle);
          if (type === 'whatsapp') url = 'https://wa.me/?text=' + encodeURIComponent(pageTitle + ' ' + pageUrl);
          if (type === 'email') url = 'mailto:?subject=' + encodeURIComponent(pageTitle) + '&body=' + encodeURIComponent(pageUrl);
          if (url) window.open(url, '_blank', 'width=600,height=400');
        });
      });

      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          if (shareUrl) {
            if (navigator.clipboard) {
              navigator.clipboard.writeText(shareUrl.value);
            } else {
              shareUrl.select();
              document.execCommand('copy');
            }
          }
          this.textContent = 'Copied!';
          var btn = this;
          setTimeout(function () { btn.textContent = 'Copy'; }, 2000);
        });
      }
    }

    // More Payment Options — click the Shopify dynamic checkout button
    var morePayBtn = document.getElementById('pdMorePayment');
    if (morePayBtn) {
      morePayBtn.addEventListener('click', function () {
        var dynamicBtn = document.querySelector('.pd-cart__dynamic .shopify-payment-button__button');
        if (dynamicBtn) {
          dynamicBtn.click();
        } else {
          // Fallback: direct checkout with selected variant
          var variantId = document.getElementById('pdVariantId');
          var qtyVal = document.getElementById('pdQuantity');
          if (variantId) {
            window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/' + variantId.value + ':' + (qtyVal ? qtyVal.value : '1');
          }
        }
      });
    }
  }

  /* ================================================================
     12. SHOPIFY ADD TO CART (AJAX)
     ================================================================ */

  function initAddToCart() {
    var productForm = document.getElementById('product-form-v2');
    if (!productForm) return;

    productForm.addEventListener('submit', function (e) {
      e.preventDefault();

      /* Clear any stale activation-date error from a previous attempt. */
      var _prevActErr = document.querySelector('[data-activation-error]');
      if (_prevActErr) _prevActErr.hidden = true;

      var variantEl = document.getElementById('pdVariantId');
      var quantityEl = document.getElementById('pdQuantity');
      // Fallback: if variant ID is empty, use first available variant
      if (variantEl && !variantEl.value && window.__PRODUCT_VARIANTS && window.__PRODUCT_VARIANTS.length > 0) {
        for (var fi = 0; fi < window.__PRODUCT_VARIANTS.length; fi++) {
          if (window.__PRODUCT_VARIANTS[fi].available) {
            variantEl.value = window.__PRODUCT_VARIANTS[fi].id;
            break;
          }
        }
      }
      if (!variantEl || !variantEl.value) {
        console.error('Add to cart: no variant selected');
        return;
      }
      var variantId = variantEl.value;
      var quantity = (quantityEl && quantityEl.value) || qty;

      var addBtn = productForm.querySelector('.pd-cart__add');
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.innerHTML = '<span class="material-symbols-outlined">hourglass_empty</span> Adding...';
      }

      /* ================================================================
         Collect line-item properties and decide single vs multi-date payload.
         ---
         Multi-date mode (qty≥2 + activation-date "same date" toggle OFF):
           data-sim-date inputs carry per-SIM dates → we build N items qty=1,
           each with its own properties[Activation Date]. Shopify's cart merges
           identical variant+properties into one line, so different-date items
           naturally stay split.
         Single mode (qty=1 OR toggle ON):
           one item qty=N with the single shared properties[Activation Date].
         ================================================================ */
      var actDateWrapper = document.querySelector('[data-activation-date-wrapper]');
      var sameDateToggle = actDateWrapper ? actDateWrapper.querySelector('[data-same-date-toggle]') : null;
      var isMultiDate = !!(actDateWrapper && sameDateToggle && !sameDateToggle.disabled && !sameDateToggle.checked);

      /* Collect shared properties (everything EXCEPT per-SIM Activation Date
         when in multi-date mode — those go on each split item). */
      var propInputs = document.querySelectorAll(
        '#product-form-v2 input[name^="properties["], ' +
        'input[form="product-form-v2"][name^="properties["]'
      );
      var sharedProperties = {};
      propInputs.forEach(function (el) {
        if (el.disabled) return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        var match = el.name.match(/^properties\[(.+)\]$/);
        if (!match) return;
        var key = match[1];
        if (isMultiDate && key === 'Activation Date') return; /* will attach per-item */
        var val = (el.value || '').trim();
        if (val === '') return;
        sharedProperties[key] = val;
      });

      var payloadItems;
      if (isMultiDate) {
        var simDateInputs = actDateWrapper.querySelectorAll('input[data-sim-date]');
        /* Validate: every row must have a date */
        var missing = Array.prototype.some.call(simDateInputs, function (i) { return !i.value; });
        if (missing || simDateInputs.length !== parseInt(quantity)) {
          console.error('Add to cart: missing per-SIM activation date(s)');
          /* Inline, screen-reader-announced error instead of a blocking alert(). */
          var actErr = actDateWrapper.querySelector('[data-activation-error]');
          if (!actErr) {
            actErr = document.createElement('div');
            actErr.setAttribute('data-activation-error', '');
            actErr.setAttribute('role', 'alert');
            actErr.className = 'pd-activation-error';
            actErr.style.cssText = 'color:#dc2626;font-size:0.875rem;font-weight:500;margin-top:8px;';
            actDateWrapper.appendChild(actErr);
          }
          actErr.textContent = 'Please select an activation date for each SIM.';
          actErr.hidden = false;
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.innerHTML = '<span class="material-symbols-outlined">shopping_cart</span> Add to Cart';
          }
          /* Focus the first empty input to guide the user */
          for (var mi = 0; mi < simDateInputs.length; mi++) {
            if (!simDateInputs[mi].value) { simDateInputs[mi].focus(); break; }
          }
          return;
        }
        payloadItems = Array.prototype.map.call(simDateInputs, function (inp) {
          var perItemProps = Object.assign({}, sharedProperties, {
            'Activation Date': inp.value
          });
          return {
            id: parseInt(variantId),
            quantity: 1,
            properties: perItemProps
          };
        });
      } else {
        var singleItem = {
          id: parseInt(variantId),
          quantity: parseInt(quantity)
        };
        if (Object.keys(sharedProperties).length > 0) singleItem.properties = sharedProperties;
        payloadItems = [singleItem];
      }

      /* PDP add-on hook: snippets (e.g. tsp-addon) push extra {id, quantity}
         items into window.__PDP_EXTRA_ITEMS to ride along in the same request. */
      if (window.__PDP_EXTRA_ITEMS && window.__PDP_EXTRA_ITEMS.length) {
        payloadItems = payloadItems.concat(window.__PDP_EXTRA_ITEMS);
      }

      fetch((window.Shopify.routes.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: payloadItems,
          /* Ask Shopify to render the cart-drawer section in the SAME response.
             Saves one full round trip — no separate /?section_id=cart-drawer fetch needed. */
          sections: 'cart-drawer'
        })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function(err){ throw new Error(err.description || err.message || 'Could not add to cart'); });
          return res.json();
        })
        .then(function (data) {
          if (addBtn) {
            addBtn.innerHTML = '<span class="material-symbols-outlined">check</span> Added!';
            setTimeout(function () {
              addBtn.disabled = false;
              addBtn.innerHTML = '<span class="material-symbols-outlined">shopping_cart</span> Add to Cart';
            }, 2000);
          }
          /* Fire-and-forget: header count updates in parallel with drawer animation */
          updateCartCount();
          /* Hand pre-rendered drawer HTML to openCartDrawer → no extra fetch */
          var drawerHtml = data.sections && data.sections['cart-drawer'];
          openCartDrawer(drawerHtml);
        })
        .catch(function (err) {
          console.error('Add to cart error:', err);
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.innerHTML = '<span class="material-symbols-outlined">shopping_cart</span> Add to Cart';
          }
        });
    });
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var countEls = document.querySelectorAll('.cart-count-bubble, [data-cart-count], .sf-cart-count, .hdr2__cart-badge');
        countEls.forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.style.display = '';
          } else {
            el.style.display = 'none';
          }
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () { /* silent */ });
  }

  function refreshCartDrawer() {
    if (_refreshPromise) return _refreshPromise;
    var container = document.getElementById('cart-drawer-container');
    if (!container) return Promise.resolve();
    /* Check if drawer is currently open before refresh */
    var oldWrapper = container.querySelector('.scd__wrapper');
    var wasOpen = oldWrapper && !oldWrapper.classList.contains('hidden');
    /* Capture current scroll position so we can restore it after DOM replacement */
    var oldScrollBody = container.querySelector('.scd__body');
    var savedScrollTop = oldScrollBody ? oldScrollBody.scrollTop : 0;

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = base + (base.endsWith('/') ? '' : '/') + '?section_id=cart-drawer';
    _refreshPromise = fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var section = temp.querySelector('#shopify-section-cart-drawer');
        container.innerHTML = section ? section.innerHTML : html;

        /* If drawer was open, keep it open after refresh */
        if (wasOpen) {
          var newWrapper = container.querySelector('.scd__wrapper');
          var newContent = container.querySelector('.scd__content');
          if (newWrapper) {
            newWrapper.classList.remove('hidden');
            newWrapper.style.setProperty('--tw-bg-opacity', '0.5');
          }
          if (newContent) {
            /* Remove translate-x-full and ensure no transition flicker */
            newContent.style.transition = 'none';
            newContent.classList.remove('translate-x-full');
            newContent.offsetHeight; /* force reflow */
            newContent.style.transition = '';
          }
          /* Restore previous scroll position so qty changes don't jump the drawer to top */
          var scrollBody = container.querySelector('.scd__body');
          if (scrollBody) scrollBody.scrollTop = savedScrollTop;
          /* Ensure scroll lock is active */
          document.documentElement.classList.add('prevent-scroll');
        }

        /* Update header cart count */
        fetch(base + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          document.querySelectorAll('.sf-cart-count').forEach(function (el) { el.textContent = cart.item_count; });
          /* Sync cart-empty state */
          var w = container.querySelector('.scd__wrapper');
          if (w) w.dataset.cartEmpty = cart.item_count > 0 ? 'false' : 'true';
        }).catch(function () {});

        if (typeof initDynamicTermsCheckbox === 'function') {
          initDynamicTermsCheckbox();
        }
      })
      .catch(function (err) {
        console.error('Cart drawer refresh failed:', err);
      })
      .finally(function () { _refreshPromise = null; });
    return _refreshPromise;
  }

  function ensureCartDrawerDelegates() {
    if (cartDrawerDelegated) return;
    cartDrawerDelegated = true;
    var container = document.getElementById('cart-drawer-container') || document;

    /* Inject pending-state CSS once: dim qty controls during in-flight cart updates
       so users can see something is processing and can't click stale buttons. */
    if (!document.getElementById('scd-updating-style')) {
      var style = document.createElement('style');
      style.id = 'scd-updating-style';
      style.textContent =
        '#cart-drawer-container.scd--updating .scd-item__quantity{opacity:.5;pointer-events:none;transition:opacity .15s}' +
        '#cart-drawer-container.scd--updating .scd-item__remove{opacity:.5;pointer-events:none}';
      document.head.appendChild(style);
    }

    function getCart() {
      return window.MinimogTheme && window.MinimogTheme.Cart ? window.MinimogTheme.Cart : null;
    }

    /* Helper: normalize root URL — always ends with / */
    var _rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (!_rootUrl.endsWith('/')) _rootUrl += '/';

    function drawerCartChange(id, quantity) {
      /* Visual lock: dim qty controls + disable pointer events while in flight.
         Prevents user from clicking on stale buttons that are about to be replaced
         by refreshCartDrawer's innerHTML swap (which causes the perceived "jump"). */
      container.classList.add('scd--updating');
      function clearLock() { container.classList.remove('scd--updating'); }
      return fetch(_rootUrl + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: id, quantity: quantity })
      }).then(function (r) {
        if (!r.ok) throw new Error('Cart change failed (' + r.status + ')');
        return r.json();
      }).then(function (cart) {
        return refreshCartDrawer().then(function () { clearLock(); return cart; });
      }).catch(function (err) {
        console.warn('[CartDrawer] drawerCartChange failed:', err);
        /* On failure, refresh drawer to resync UI with actual cart state */
        return refreshCartDrawer().then(clearLock, clearLock);
      });
    }
    function drawerUpdateNote(note) {
      return fetch(_rootUrl + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note })
      }).then(function (r) {
        if (!r.ok) throw new Error('Note save failed (' + r.status + ')');
        return r.json();
      }).then(function (cart) {
        return refreshCartDrawer().then(function () { return cart; });
      });
      /* No .catch here — let caller handle errors */
    }

    /* Show inline error message inside an addon panel */
    function showAddonError(addon, msg) {
      if (!addon) return;
      var el = addon.querySelector('.scd__addon-error');
      if (!el) {
        el = document.createElement('div');
        el.className = 'scd__addon-error';
        el.style.cssText = 'color:#dc2626;font-size:0.8rem;font-weight:600;padding:8px 12px;margin-top:8px;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;';
        var actions = addon.querySelector('.scd__addon-actions');
        if (actions) { addon.insertBefore(el, actions); } else { addon.appendChild(el); }
      }
      el.textContent = msg;
      el.style.display = 'block';
    }
    function clearAddonError(addon) {
      if (!addon) return;
      var el = addon.querySelector('.scd__addon-error');
      if (el) el.style.display = 'none';
    }

    /* --- Disable Minimog Cart's drawer event delegates ---
       Minimog binds on .scd__wrapper which gets destroyed by innerHTML refresh.
       Before first refresh, both systems fire causing double qty changes.
       Solution: neuter Minimog's drawer handlers by clearing its domNodes. */
    (function disableMinimogDrawerHandlers() {
      var mc = getCart();
      if (mc && mc.domNodes && mc.domNodes.cartDrawer) {
        mc.domNodes.cartDrawer = null;
      }
    })();

    /* Debounce helper for qty changes */
    var _drawerChangeTimer = null;
    function debouncedDrawerCartChange(id, qty) {
      clearTimeout(_drawerChangeTimer);
      _drawerChangeTimer = setTimeout(function () {
        drawerCartChange(id, qty);
      }, 300);
    }

    /* --- Click delegation on #cart-drawer-container --- */
    container.addEventListener('click', function (e) {
      /* Close button */
      if (e.target.closest('.scd__close')) {
        e.preventDefault();
        closeCartDrawer();
        return;
      }

      /* Overlay click to close (click directly on .scd__wrapper, not its children) */
      var wrapper = e.target.closest('.scd__wrapper');
      if (wrapper && e.target === wrapper) {
        closeCartDrawer();
        return;
      }

      /* Addon Cancel button — must be checked BEFORE other handlers */
      var cancelBtn = e.target.closest('.btn-cancel');
      if (cancelBtn && container.contains(cancelBtn)) {
        e.preventDefault();
        e.stopPropagation();
        container.querySelectorAll('.scd__addon.open').forEach(function (a) { a.classList.remove('open'); });
        container.querySelectorAll('[data-open].active').forEach(function (b) { b.classList.remove('active'); });
        return;
      }

      /* Addon Save button */
      var saveBtn = e.target.closest('.btn-save');
      if (saveBtn && container.contains(saveBtn)) {
        e.preventDefault();
        e.stopPropagation();
        var actionType = saveBtn.getAttribute('data-action');
        var saveBtnText = saveBtn.textContent;
        saveBtn.textContent = '...';
        saveBtn.disabled = true;

        function closeAddonPanel() {
          saveBtn.textContent = saveBtnText;
          saveBtn.disabled = false;
          container.querySelectorAll('.scd__addon.open').forEach(function (a) { a.classList.remove('open'); });
          container.querySelectorAll('[data-open].active').forEach(function (b) { b.classList.remove('active'); });
        }

        var addon = saveBtn.closest('.scd__addon');

        if (actionType === 'note') {
          var noteInput = container.querySelector('[name="note"]');
          clearAddonError(addon);
          if (noteInput) {
            drawerUpdateNote(noteInput.value)
              .then(function () { closeAddonPanel(); })
              .catch(function (err) {
                console.warn('[CartDrawer] Note save failed:', err);
                saveBtn.textContent = saveBtnText;
                saveBtn.disabled = false;
                showAddonError(addon, 'Failed to save note. Please try again.');
              });
          } else {
            closeAddonPanel();
          }
        } else if (actionType === 'coupon') {
          var codeInput = container.querySelector('.scd__discount_code');
          clearAddonError(addon);
          if (codeInput && codeInput.value.trim()) {
            var code = codeInput.value.trim();
            /* Step 1: Get cart discount codes BEFORE applying */
            fetch(_rootUrl + 'cart.js').then(function (r) { return r.json(); }).then(function (cartBefore) {
              var beforeCodes = (cartBefore.discount_codes || []).map(function (d) { return d.code.toUpperCase(); });
              /* Step 2: Apply discount via /discount/CODE */
              return fetch(_rootUrl + 'discount/' + encodeURIComponent(code), { redirect: 'follow', credentials: 'same-origin' })
                .then(function () {
                  /* Step 3: Check cart AFTER to verify discount was applied */
                  return fetch(_rootUrl + 'cart.js').then(function (r) { return r.json(); });
                })
                .then(function (cartAfter) {
                  var afterCodes = (cartAfter.discount_codes || []).map(function (d) { return d.code.toUpperCase(); });
                  var codeUpper = code.toUpperCase();
                  if (afterCodes.indexOf(codeUpper) !== -1) {
                    /* Success: code is now in cart */
                    return refreshCartDrawer().then(function () { closeAddonPanel(); });
                  } else {
                    /* Code was not applied — invalid or not applicable */
                    saveBtn.textContent = saveBtnText;
                    saveBtn.disabled = false;
                    showAddonError(addon, 'Invalid discount code. Please check and try again.');
                  }
                });
            }).catch(function (err) {
              console.warn('[CartDrawer] Coupon apply failed:', err);
              saveBtn.textContent = saveBtnText;
              saveBtn.disabled = false;
              showAddonError(addon, 'Something went wrong. Please try again.');
            });
          } else {
            saveBtn.textContent = saveBtnText;
            saveBtn.disabled = false;
            showAddonError(addon, 'Please enter a discount code.');
          }
        } else {
          closeAddonPanel();
        }
        return;
      }

      /* Qty +/- buttons */
      var qtyBtn = e.target.closest('.scd-item__btn');
      if (qtyBtn) {
        e.preventDefault();
        e.stopPropagation();
        var id = qtyBtn.getAttribute('data-id');
        if (!id) return;
        var qtyWrap = qtyBtn.closest('.scd-item__quantity');
        var input = qtyWrap ? qtyWrap.querySelector('.scd-item__qty_input') : null;
        if (!input) return;
        var qty = parseInt(input.value) || 1;
        qty += (qtyBtn.getAttribute('data-qty-change') === 'dec' ? -1 : 1);
        if (qty < 1) qty = 1;
        if (qty > 99) qty = 99;
        input.value = qty;
        /* AJAX + full drawer refresh, debounced for rapid clicks */
        debouncedDrawerCartChange(id, qty);
        return;
      }

      /* Remove button */
      var removeBtn = e.target.closest('.scd-item__remove');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        var id = removeBtn.getAttribute('data-id');
        /* Always use AJAX + full drawer refresh */
        drawerCartChange(id, 0);
        return;
      }

      /* Addon open buttons (Note / Coupon / Shipping) */
      var openBtn = e.target.closest('[data-open]');
      if (openBtn && container.contains(openBtn)) {
        e.stopPropagation();
        var action = openBtn.getAttribute('data-open');
        var addon = container.querySelector('#scd-' + action);
        if (!addon) return;
        var openAddon = container.querySelector('.scd__addon.open');
        if (openAddon && openAddon !== addon) {
          openAddon.classList.remove('open');
          container.querySelectorAll('[data-open]').forEach(function (b) { b.classList.remove('active'); });
        }
        addon.classList.toggle('open');
        openBtn.classList.toggle('active');
        return;
      }
    });

    /* --- Change delegation for qty input --- */
    container.addEventListener('change', function (e) {
      var input = e.target.closest('.scd-item__qty_input');
      if (!input) return;
      e.preventDefault();
      var id = input.getAttribute('data-id');
      var qty = parseInt(input.value) || 1;
      if (qty < 1) qty = 1;
      if (qty > 99) { qty = 99; input.value = 99; }
      drawerCartChange(id, qty);
    });
  }

  function closeCartDrawer() {
    var wrapper = document.querySelector('#cart-drawer-container .scd__wrapper');
    var content = document.querySelector('#cart-drawer-container .scd__content');
    if (!wrapper || !content) return;
    wrapper.style.setProperty('--tw-bg-opacity', '0');
    content.classList.add('translate-x-full');
    setTimeout(function () {
      wrapper.classList.add('hidden');
      document.documentElement.classList.remove('prevent-scroll');
    }, 300);
  }

  function openCartDrawer(preloadedHtml) {
    ensureCartDrawerDelegates();

    var readyPromise;
    if (preloadedHtml) {
      /* Drawer HTML already came with the cart/add.js response — inject it
         directly and skip the extra network round trip. */
      var container = document.getElementById('cart-drawer-container');
      if (container) {
        var temp = document.createElement('div');
        temp.innerHTML = preloadedHtml;
        var sectionEl = temp.querySelector('#shopify-section-cart-drawer');
        container.innerHTML = sectionEl ? sectionEl.innerHTML : preloadedHtml;
      }
      readyPromise = Promise.resolve();
    } else {
      readyPromise = refreshCartDrawer().catch(function () { /* ignore */ });
    }

    readyPromise.then(function () {
      var wrapper = document.querySelector('#cart-drawer-container .scd__wrapper');
      var content = document.querySelector('#cart-drawer-container .scd__content');
      if (wrapper && content) {
        document.documentElement.classList.add('prevent-scroll');
        wrapper.classList.remove('hidden');
        requestAnimationFrame(function () {
          wrapper.style.setProperty('--tw-bg-opacity', '0.5');
          content.classList.remove('translate-x-full');
        });
      } else {
        var cartIcon = document.querySelector('.sf-cart-icon');
        if (cartIcon) {
          setTimeout(function () { cartIcon.click(); }, 300);
        }
      }
      if (typeof window.FoxTheme !== 'undefined' && window.FoxTheme.Notification) {
        window.FoxTheme.Notification.show({ type: 'success', message: 'Added to cart!' });
      }
    });
  }

  /* ================================================================
     13. BUY NOW (DIRECT CHECKOUT)
     ================================================================ */

  function initBuyNow() {
    var buyBtn = document.querySelector('.pd-cart__buy');
    if (!buyBtn) return;

    buyBtn.addEventListener('click', function () {
      var variantEl = document.getElementById('pdVariantId');
      var quantityEl = document.getElementById('pdQuantity');
      if (variantEl && !variantEl.value && window.__PRODUCT_VARIANTS && window.__PRODUCT_VARIANTS.length > 0) {
        for (var fi = 0; fi < window.__PRODUCT_VARIANTS.length; fi++) {
          if (window.__PRODUCT_VARIANTS[fi].available) { variantEl.value = window.__PRODUCT_VARIANTS[fi].id; break; }
        }
      }
      if (!variantEl || !variantEl.value) return;
      var variantId = variantEl.value;
      var quantity = (quantityEl && quantityEl.value) || qty;
      window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/' + variantId + ':' + quantity;
    });
  }

  /* ================================================================
     INIT ON DOM READY
     ================================================================ */

  function init() {
    // Critical path: plan selector + cart functionality (must run immediately)
    buildPlanSelector();
    initQuantity();
    initConfirmCheckbox();
    initAddToCart();
    initBuyNow();

    // Lightweight event delegation (cheap, needed for interaction)
    initTabs();
    initFaq();
    initAccordion();
    initCardWishlistButtons();
    ensureCartDrawerDelegates();

    // Defer non-critical initializations to after first paint
    requestAnimationFrame(function () {
      initDeferredModals();
    });
  }

  /**
   * Deferred modal initialization — Device Compatibility, Ask, Share modals
   * are hidden on load. Only set up open-button listeners initially;
   * full modal init happens on first trigger click.
   */
  function initDeferredModals() {
    // Device modal: defer full init until first open
    var dcModalInited = false;
    document.querySelectorAll('.sec-steps__compat-btn, .pd-compat-btn, [data-open-dc-modal]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!dcModalInited) {
          dcModalInited = true;
          initDeviceModal();
        }
        // After init, re-trigger the open
        var modal = document.getElementById('dc-modal');
        if (modal) {
          var mobilePanel = document.getElementById('hdr2MobilePanel');
          if (mobilePanel) mobilePanel.classList.remove('is-open');
          modal.classList.add('active');
          document.body.style.overflow = 'hidden';
        }
      });
    });

    // Action buttons (wishlist needs immediate localStorage check, modals can defer)
    initActionButtons();
  }

  /* ================================================================
     CARD WISHLIST — unified sf-wishlist for product cards site-wide
     ================================================================ */
  function initCardWishlistButtons() {
    var WL_KEY = 'sf-wishlist';
    function getWL() { try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch(e) { return []; } }
    function saveWL(list) { localStorage.setItem(WL_KEY, JSON.stringify(list)); }
    function syncBadges() {
      var count = getWL().length;
      document.querySelectorAll('.sf-wishlist-count, .ssw-counter-fave-menu').forEach(function(b) {
        if (count > 0) { b.textContent = count; b.style.display = ''; }
        else { b.textContent = ''; b.style.display = 'none'; }
      });
    }

    /* Mark already-wishlisted cards */
    var list = getWL();
    document.querySelectorAll('.pc-wishlist-btn[data-wishlist-handle]').forEach(function(btn) {
      var handle = btn.getAttribute('data-wishlist-handle');
      if (list.indexOf(handle) >= 0) btn.classList.add('is-wishlisted');
    });

    /* Delegate clicks on document (cards may be in any section) */
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.pc-wishlist-btn[data-wishlist-handle]');
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-wishlist-handle');
      if (!handle) return;
      var wl = getWL();
      var idx = wl.indexOf(handle);
      if (idx >= 0) {
        wl.splice(idx, 1);
        btn.classList.remove('is-wishlisted');
      } else {
        wl.push(handle);
        btn.classList.add('is-wishlisted');
      }
      saveWL(wl);
      syncBadges();
    });

    /* Initial badge sync */
    syncBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
